import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const API_URL = process.env.REACT_APP_API_URL;

const formatPostDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toISOString().split("T")[0]; // YYYY-MM-DD
};

const UserReport = () => {
  const [userEmail, setUserEmail] = useState("");
  const [activities, setActivities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [error, setError] = useState("");

  const fetchReport = async () => {
    setError("");
    setActivities([]);
    setPayments([]);
    setAccountInfo(null);

    if (!userEmail.trim()) {
      setError("Please enter a valid email");
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/user-report/${encodeURIComponent(
          userEmail.trim()
        )}`
      );

      if (!res.ok) throw new Error("Network error");

      const data = await res.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      if (!data.activities || !data.activities.length) {
        setError("No activities found");
        return;
      }

      setActivities(data.activities);
      setPayments(data.payments || []);
      setAccountInfo(data.account || null);
    } catch {
      setError("Failed to fetch report");
    }
  };

  // Calculate payable amount for an activity (wordcounts * rate)
  const calcPayable = (a) => {
    const wc = parseFloat(a.wordcounts) || 0;
    const rate = parseFloat(a.rate) || 0;
    return wc * rate;
  };

  // Total calculations
  const totalPayable = activities.reduce(
    (acc, a) => acc + (parseFloat(a.payable_amount) || calcPayable(a)),
    0
  );
  const totalPaid = payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const balance = totalPayable - totalPaid;

  const downloadPDF = () => {
    if (!activities.length && !payments.length) return;
  
    const doc = new jsPDF();
  
    doc.setFontSize(14);
    doc.text(`User Report - ${userEmail}`, 14, 15);
  
    let yOffset = 25;
  
    if (accountInfo) {
      const infoLines = [
        `Account No: ${accountInfo.account_no || "-"}`,
        `IFSC: ${accountInfo.ifsc || "-"}`,
        `PAN: ${accountInfo.pan || "-"}`,
      ];
      doc.setFontSize(10);
      infoLines.forEach((line, i) => doc.text(line, 14, yOffset + i * 6));
      yOffset += infoLines.length * 6 + 5;
    }
  
    // Activities Table with right-aligned numeric columns
    autoTable(doc, {
      startY: yOffset,
      head: [["Project ID", "Activity", "Wordcounts", "Rate", "Payable Amount"]],
      body: activities.map(a => {
      const wc = parseFloat(a.wordcounts) || 0;
      const rate = parseFloat(a.rate) || 0;
      const payable = parseFloat(a.payable_amount) || wc * rate;
      return [
        a.projectid || "-",
        a.activity || "-",
        wc,
        rate.toFixed(2),
        payable.toFixed(2), // Ensure two decimal places for consistency
      ];
      }),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, halign: "center" },
      columnStyles: {
      0: { halign: "center" }, // Project ID right aligned
      1: { halign: "center" },
      2: { halign: "center" }, // Wordcounts right aligned
      3: { halign: "center" }, // Rate right aligned
      4: { halign: "center" },  // Payable Amount left aligned
      },
      margin: { left: 20, right: 10 },
      pageBreak: "auto",
    });
    
    const nextY = (doc.lastAutoTable?.finalY ?? yOffset) + 10;
  
    // Payments Table with right-aligned Amount column
    autoTable(doc, {
      startY: nextY,
      head: [["Amount", "Post Date", "Particulars"]],
      body: payments.map(p => [
        (parseFloat(p.amount) || 0).toFixed(2),
        formatPostDate(p.postDate),
        p.particulars || "",
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [39, 174, 96], textColor: 255, halign: "center" },
      columnStyles: {
        0: { halign: "center" }, // Amount right aligned
        1: {halign: "center"},
        2: {halign: "center"}
      },
      margin: { left: 20, right: 10 },
      pageBreak: "auto",
    });
  
    // Summary section with proper formatting and alignment
    const summaryY = (doc.lastAutoTable?.finalY ?? 20) + 10;
    const lineHeight = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginLeft = 14;
    const marginRight = 14;
    const valueX = pageWidth - marginRight - 10; // inside margin
  
    doc.setFontSize(11);
  
    doc.text("Total Payable Amount:", marginLeft, summaryY);
    doc.text(`Rs. ${totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, valueX, summaryY, { align: "right" });
    
    doc.text("Total Paid Amount:", marginLeft, summaryY + lineHeight);
    doc.text(`Rs. ${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, valueX, summaryY + lineHeight, { align: "right" });
    
    doc.text("Balance Amount:", marginLeft, summaryY + 2 * lineHeight);
    doc.text(`Rs. ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, valueX, summaryY + 2 * lineHeight, { align: "right" });
  
    doc.save(`User_Report_${userEmail.replace(/[^a-z0-9]/gi, "_")}.pdf`);
  };
  
  

  return (
    <div className="container mt-4">
      <h3>User-wise Report Generator</h3>

      <div className="row mb-3">
        <div className="col-md-6">
          <input
            type="email"
            className="form-control"
            placeholder="Enter user email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary" onClick={fetchReport}>
            Fetch Report
          </button>
        </div>
        {(activities.length > 0 || payments.length > 0) && (
          <div className="col-md-3 text-end">
            <button className="btn btn-success" onClick={downloadPDF}>
              Download PDF
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {accountInfo && (
        <div className="mb-4 p-3 bg-light border rounded">
          <h5>User Info</h5>
          <p><strong>Email:</strong> {userEmail}</p>
          <p><strong>Account No:</strong> {accountInfo.account_no || "-"}</p>
          <p><strong>IFSC:</strong> {accountInfo.ifsc || "-"}</p>
          <p><strong>PAN:</strong> {accountInfo.pan || "-"}</p>
        </div>
      )}

      {(activities.length > 0 || payments.length > 0) && (
        <>
          <h5>Activities</h5>
          <div className="table-responsive mb-4">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>Project ID</th>
                  <th>Activity</th>
                  <th>Wordcounts</th>
                  <th>Rate</th>
                  <th>Payable Amount</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((a, i) => {
                  const wc = parseFloat(a.wordcounts) || 0;
                  const rate = parseFloat(a.rate) || 0;
                  const payable = parseFloat(a.payable_amount) || wc * rate;
                  return (
                    <tr key={i}>
                      <td>{a.projectid || "-"}</td>
                      <td>{a.activity || "-"}</td>
                      <td>{wc}</td>
                      <td>{rate.toFixed(2)}</td>
                      <td>{payable.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h5>Payments</h5>
          <div className="table-responsive mb-4">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>Amount</th>
                  <th>Post Date</th>
                  <th>Particulars</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={i}>
                    <td>{(parseFloat(p.amount) || 0).toFixed(2)}</td>
                    <td>{formatPostDate(p.postDate)}</td>
                    <td>{p.particulars || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-3 border bg-light">
            <h5>Summary</h5>
            <p><strong>Total Payable Amount:</strong> ₹{totalPayable.toFixed(2)}</p>
            <p><strong>Total Paid Amount:</strong> ₹{totalPaid.toFixed(2)}</p>
            <p><strong>Balance Amount:</strong> ₹{balance.toFixed(2)}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default UserReport;
